// L2.6 — WHEN THE CREATE-YOUR-ROOM BOX IS ON HER SCREEN, AND WHEN IT IS NOT.
//
// HER RULING (launch day, 2026-08-05): "on mobile the signup/details box cannot be closed, no X,
// returns on refresh, blocking a visitor who wants to browse before committing. The box gets an X on
// mobile AND desktop, dismissing lets them browse the public site freely, and the box re-offers
// naturally at gated actions (joining, generating) rather than camping the screen."
//
// **THIS OVERRIDES A DELIBERATE DESIGN, AND THE DESIGN SAID SO IN AS MANY WORDS.**
// `src/components/ModalBackdrop.tsx` argues its optional `onClose` like this: *"a backdrop with no
// `onClose` — the onboarding gate, the room-build progress — is a modal that must not be dismissible,
// and it is dismissible by NEITHER route because both effects read the same prop."* So the box was
// undismissable ON PURPOSE and by construction; her ruling replaces that intent, and this module is
// where the replacement lives rather than as a flipped boolean nobody can find later.
//
// **THE WHOLE DIFFICULTY IS THAT "DISMISSED" MUST NOT MEAN "GONE".** A dismissal that persists
// everywhere is a member who can never find her way back to the one screen that makes her a member;
// a dismissal that dies on the next navigation is the camping she reported. So dismissal is scoped to
// WHERE SHE IS: it holds while she is on a page the public may read, and it lapses the moment she
// asks for something that needs a room. That is her "re-offers naturally at gated actions" expressed
// as arithmetic instead of as a list of call sites somebody has to remember to update — a new gated
// page re-offers by EXISTING, which is the shape this house reaches for every time (R17/R28/R29).
//
// It imports ONLY the page roster and a type, so `scripts/` can load and RUN it.
import { PAGE_PATHS, type Page } from "./pages";

// **THE BROWSE SET IS DECLARED HERE AND IS DELIBERATELY *NOT* `OPEN_PAGES`, WHICH IS THE ONE THING
// THIS MODULE GOT WRONG BEFORE ITS OWN FIXTURE ASKED.** `OPEN_PAGES` answers *does this page cost a
// TIER FEATURE* — and `my-room` is in it, because My Room is free. That is a different question from
// *can a member with no room do anything here*, and borrowing it would have been this house's
// most-paid shape exactly: a guard answering an easier question than the thing it guards
// (R30/R95/R116/R117/R146b/R148/R150/R158/R160/D-5/W7-2/R181/R183). Under it, dismissing the box on
// the home page would have left My Room a blank shell with no way to get the box back.
//
// So: the pages a signed-up member with NO ROOM can actually READ. Everything else — My Room, every
// reading room, the Magic Room, the Living Room, the daily — is her "gated action", because the thing
// it does is the thing a room is required for.
export const BROWSE_PAGES: Page[] = ["home", "terms", "privacy", "faq", "contact", "membership"];

// HER /talk FUNNEL, 2026-08-26. She submitted her details and met the CREATE-YOUR-ROOM box instead
// of the plum window. TalkCapture calls onMember the instant /api/auth/login answers, so isLoggedIn
// is TRUE while the room is still being made and the chart is still casting -- but roomProfile is
// not set until onArrive, seconds later. In that window hasRoom is false, page is "talk", talk is
// not a browse page, and this gate drew the box over her glowing wait state.
//
// Scoped to the funnel's own two pages so it can never camp: onCapturing(false) fires on the
// FAILURE arm only, so an unscoped flag would stay true all session and a member whose room really
// did fail would never be offered the box again.
export const FUNNEL_PAGES: Page[] = ["talk", "ask-lala"];

export const funnelOwnsScreen = (state: Pick<OnboardState, "funnelCapturing" | "page">): boolean =>
  state.funnelCapturing === true && FUNNEL_PAGES.includes(state.page);

export type OnboardState = {
  isLoggedIn: boolean;
  funnelCapturing?: boolean;
  profileChecked: boolean;
  hasRoom: boolean;
  buildStatus: string;
  membershipOpen: boolean;
  dismissed: boolean;
  page: Page;
};

// A page a member with no room can actually read. Anything else is a gated action in her sense — My
// Room, a generator, a reading room — and asking for one is asking for the thing the box exists to
// give. **The default is GATED**: a page nobody classified re-offers the box, which is the recoverable
// direction (she is asked again) rather than the silent one (a blank room she cannot fix).
export const isPublicPage = (page: Page): boolean => BROWSE_PAGES.includes(page);

// Every page in the roster is a real page. A typo here would silently make a gated page browsable —
// the direction that leaves her stuck — so the set is held against the one place pages are declared.
export const browsePagesAreReal = (): boolean =>
  BROWSE_PAGES.every((p) => Object.prototype.hasOwnProperty.call(PAGE_PATHS, p));

// **A DISMISSAL ONLY HOLDS ON A PUBLIC PAGE.** This is the one clause her ruling turns on, and it is
// deliberately not "dismissed forever" and not "dismissed until navigation".
export const dismissalHolds = (state: Pick<OnboardState, "dismissed" | "page">): boolean =>
  Boolean(state.dismissed) && isPublicPage(state.page);

// The gate itself. Every clause that was in the JSX condition is here, in the same order, plus hers.
export function showOnboarding(state: OnboardState): boolean {
  if (!state.isLoggedIn) return false;
  // R18: "we have not looked yet" is not "she has no room". Drawing the box over an unread profile
  // is how a member with a room is asked to make a second one.
  if (!state.profileChecked) return false;
  if (state.hasRoom) return false;
  // The build overlay owns the screen while a room is being made; two full-screen layers is one lie.
  if (state.buildStatus !== "idle") return false;
  // She opened the membership box herself — that is a deliberate act and it outranks this offer.
  if (state.membershipOpen) return false;
  // Her funnel is carrying her right now -- the glow is the screen she is meant to be watching.
  if (funnelOwnsScreen(state)) return false;
  // >>> AND HER OWN DASHBOARD IS A WORK SURFACE, NOT A PAGE TO BE ASKED ON (2026-08-19). <<<
  // This box is UNDISMISSABLE anywhere that is not a public page (`dismissalHolds`, one law up) —
  // right for a member being carried into her room, and a WALL over an owner's own business screen.
  // Traced at coordinates on production: it covered a live Send control at both viewports, so her
  // report of *no send path* was a control she could see and could not reach. An owner with no room
  // is a real state (her `lala@aquarius.io` seat is exactly that), and the offer is not lost — every
  // other page still makes it, including the one this box exists to carry her to.
  if (state.page === "dashboard") return false;
  if (dismissalHolds(state)) return false;
  return true;
}

// WHERE THE DISMISSAL IS KEPT, AND WHY IT IS NOT `localStorage`. Her complaint names "returns on
// refresh" as part of the defect, so it has to survive one; but a dismissal kept forever means the
// box never re-offers on a later visit, which is the opposite failure and the more expensive one —
// this is the screen that turns a signed-up stranger into a member. `sessionStorage` is exactly the
// middle: it survives a refresh and a whole browse, and a new session offers again.
export const ONBOARD_DISMISS_KEY = "twelve_house_onboard_dismissed";

// `store` is a PARAMETER so a gate RUNS this rather than reading it (R46/R63's shape). A storage that
// throws — Safari in private mode does — must never take the signup screen down with it, so both
// halves are guarded and a failure reads as "not dismissed", which is the SAFE direction: the worst
// case is that she is offered the box again, never that she cannot reach it.
export function readDismissed(store: Pick<Storage, "getItem"> | null | undefined): boolean {
  try {
    return store?.getItem(ONBOARD_DISMISS_KEY) === "1";
  } catch {
    return false;
  }
}

export function writeDismissed(store: Pick<Storage, "setItem" | "removeItem"> | null | undefined, dismissed: boolean): void {
  try {
    if (dismissed) store?.setItem(ONBOARD_DISMISS_KEY, "1");
    else store?.removeItem(ONBOARD_DISMISS_KEY);
  } catch {
    /* a storage that refuses is not a reason to lose the screen */
  }
}
